import React, { useState, useCallback, useEffect } from 'react';
import { useApi } from '../../api/useApi';

// Definice CRUD číselníků
export type CodeListKey =
  | 'applicants'
  | 'externalDepartments'
  | 'externalOrganizations'
  | 'deathClassifications'
  | 'toxicologists';

export type ColumnDef = {
  key: string;
  label: string;
  render?: (value: any, row: any) => React.ReactNode;
};

export type FieldDef = {
  key: string;
  label: string;
  type: 'text' | 'number' | 'checkbox';
  required?: boolean;
};

export type CodeListConfig = {
  key: CodeListKey;
  label: string;
  columns: ColumnDef[];
  fields: FieldDef[];
};

export const CODE_LISTS: CodeListConfig[] = [
  {
    key: 'applicants',
    label: 'Žadatelé',
    columns: [
      { key: 'name', label: 'Název' },
      { key: 'ico', label: 'IČO' },
      { key: 'address_line1', label: 'Adresa' },
      { key: 'contact_person', label: 'Kontaktní osoba' },
      { key: 'contact', label: 'Kontakt' },
      { key: 'is_active', label: 'Aktivní' }
    ],
    fields: [
      { key: 'external_id', label: 'Externí ID', type: 'number', required: true },
      { key: 'name', label: 'Název', type: 'text', required: true },
      { key: 'expertise', label: 'Odbornost', type: 'text' },
      { key: 'ico', label: 'IČO', type: 'text' },
      { key: 'dic', label: 'DIČ', type: 'text' },
      { key: 'address_line1', label: 'Adresa (řádek 1)', type: 'text' },
      { key: 'address_line2', label: 'Adresa (řádek 2)', type: 'text' },
      { key: 'country', label: 'Země', type: 'text' },
      { key: 'contact', label: 'Kontakt', type: 'text' },
      { key: 'contact_person', label: 'Kontaktní osoba', type: 'text' },
      { key: 'description', label: 'Poznámka', type: 'text' },
      { key: 'is_active', label: 'Aktivní', type: 'checkbox' }
    ]
  },
  {
    key: 'externalDepartments',
    label: 'Externí oddělení',
    columns: [
      { key: 'department', label: 'Oddělení' },
      { key: 'icp', label: 'IČP' },
      { key: 'organization', label: 'Organizace' },
      { key: 'specialization_code', label: 'Kód specializace' },
      { key: 'is_active', label: 'Aktivní' }
    ],
    fields: [
      { key: 'department', label: 'Oddělení', type: 'text', required: true },
      { key: 'icp', label: 'IČP', type: 'text', required: true },
      { key: 'organization', label: 'Název organizace', type: 'text' },
      { key: 'specialization_code', label: 'Kód specializace', type: 'text' },
      { key: 'is_active', label: 'Aktivní', type: 'checkbox' }
    ]
  },
  {
    key: 'externalOrganizations',
    label: 'Externí organizace',
    columns: [
      { key: 'name', label: 'Název' },
      { key: 'icz', label: 'IČZ' },
      { key: 'is_active', label: 'Aktivní' }
    ],
    fields: [
      { key: 'name', label: 'Název', type: 'text', required: true },
      { key: 'icz', label: 'IČZ', type: 'text', required: true },
      { key: 'is_active', label: 'Aktivní', type: 'checkbox' }
    ]
  },
  {
    key: 'deathClassifications',
    label: 'Klasifikace úmrtí',
    columns: [
      { key: 'name', label: 'Název' },
      { key: 'is_active', label: 'Aktivní' }
    ],
    fields: [
      { key: 'name', label: 'Název', type: 'text', required: true },
      { key: 'is_active', label: 'Aktivní', type: 'checkbox' }
    ]
  },
  {
    key: 'toxicologists',
    label: 'Toxikologové',
    columns: [
      {
        key: 'employee',
        label: 'Jméno',
        render: (_: any, row: any) => row.employee?.name ?? '—'
      },
      { key: 'employee_card_number',
        label: 'Číslo průkazu',
        render: (_: any, row: any) => row.employee?.card_number ?? '—'
      },
      { key: 'is_active', label: 'Aktivní' }
    ],
    fields: [
      { key: 'employee.external_id', label: 'Externí ID', type: 'number' },
      { key: 'employee.name', label: 'Jméno', type: 'text', required: true },
      { key: 'employee.card_number', label: 'Číslo průkazu', type: 'text' },
      { key: 'is_active', label: 'Aktivní', type: 'checkbox' }
    ]
  }
];

const DEFAULT_LIMIT = 100;

export const useCodeList = () => {
  const api = useApi();
  const [selectedCodeList, setSelectedCodeList] = useState<CodeListKey>(CODE_LISTS[0].key);
  const [data, setData] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const config = CODE_LISTS.find((cl) => cl.key === selectedCodeList)!;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resource = api.references[selectedCodeList] as any;
      const response = await resource.get({ limit, offset });
      setData(response.data.results ?? []);
      setCount(response.data.count ?? 0);
    } catch {
      setData([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [selectedCodeList, limit, offset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCodeListChange = (key: CodeListKey) => {
    setSelectedCodeList(key);
    setPage(1);
    setOffset(0);
  };

  const handlePagination = (newOffset: number, newPage: number) => {
    setOffset(newOffset);
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
    setOffset(0);
  };

  const openCreate = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  const openEdit = (item: any) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const flattenObject = (value: Record<string, any>): Record<string, any> =>
    Object.entries(value ?? {}).reduce<Record<string, any>>((acc, [key, nestedValue]) => {
      if (nestedValue && typeof nestedValue === 'object' && !Array.isArray(nestedValue)) {
        Object.assign(acc, flattenObject(nestedValue));
      } else {
        acc[key] = nestedValue;
      }

      return acc;
    }, {});

  const saveItem = async (formData: Record<string, any>) => {
    const resource = api.references[selectedCodeList] as any;
    const payload = flattenObject(editingItem ? { ...editingItem, ...formData } : formData);
    if (editingItem) {
      await resource.put(editingItem.id, payload);
    } else {
      await resource.post(payload);
    }
    closeModal();
    fetchData();
  };

  const requestDelete = (id: number) => {
    setConfirmDeleteId(id);
  };

  const confirmDelete = async () => {
    if (confirmDeleteId === null) return;
    const resource = api.references[selectedCodeList] as any;
    await resource.remove(confirmDeleteId);
    setConfirmDeleteId(null);
    fetchData();
  };

  const cancelDelete = () => {
    setConfirmDeleteId(null);
  };

  return {
    config,
    selectedCodeList,
    data,
    count,
    loading,
    page,
    offset,
    limit,
    modalOpen,
    editingItem,
    confirmDeleteId,
    handleCodeListChange,
    handlePagination,
    handleLimitChange,
    openCreate,
    openEdit,
    closeModal,
    saveItem,
    requestDelete,
    confirmDelete,
    cancelDelete
  };
};
